import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';

import {
  deactivateTicketCategory,
  getApiErrorMessage,
  listTicketCategories,
} from '../../api/client';
import type { TicketCategoryDefinition } from '../../api/types';
import Badge from '../../components/Badge';
import { useAuth } from '../../contexts/AuthContext';
import { canEditPlatformConfig, canWritePlatformConfig } from '../../utils/permissions';
import { PLATFORM_CONFIG } from '../platform-config/paths';

export default function TicketCategoriesPage() {
  const { user } = useAuth();
  const [categories, setCategories] = useState<TicketCategoryDefinition[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const canCreate = user && canWritePlatformConfig(user);
  const canModify = user && canEditPlatformConfig(user);

  useEffect(() => {
    listTicketCategories()
      .then(setCategories)
      .catch((err) => setError(getApiErrorMessage(err)))
      .finally(() => setLoading(false));
  }, []);

  async function handleDeactivate(id: string, name: string) {
    if (!window.confirm(`Deactivate category "${name}"? Existing tickets keep it; new tickets cannot use it.`)) {
      return;
    }
    try {
      const updated = await deactivateTicketCategory(id);
      setCategories((prev) => prev.map((c) => (c.id === id ? updated : c)));
    } catch (err) {
      setError(getApiErrorMessage(err));
    }
  }

  return (
    <>
      {canCreate && (
        <div className="page-header" style={{ marginTop: 0 }}>
          <div />
          <Link to={PLATFORM_CONFIG.categoriesNew} className="btn btn-primary">
            Add category
          </Link>
        </div>
      )}
      {error && <div className="alert alert-error">{error}</div>}
      <div className="card">
        {loading ? (
          <div className="loading">Loading categories…</div>
        ) : (
          <table className="data-table">
            <thead>
              <tr>
                <th>Code</th>
                <th>Name</th>
                <th>Tickets</th>
                <th>Status</th>
                {canModify && <th />}
              </tr>
            </thead>
            <tbody>
              {categories.map((c) => (
                <tr key={c.id}>
                  <td>
                    {canModify ? (
                      <Link to={PLATFORM_CONFIG.category(c.id)}>{c.code}</Link>
                    ) : (
                      c.code
                    )}
                    {c.isSystem && (
                      <>
                        {' '}
                        <Badge label="SYSTEM" className="badge-system" />
                      </>
                    )}
                    {c.isDefault && (
                      <>
                        {' '}
                        <Badge label="DEFAULT" className="badge-neutral" />
                      </>
                    )}
                  </td>
                  <td>{c.name}</td>
                  <td>{c._count?.tickets ?? '—'}</td>
                  <td>
                    <Badge
                      label={c.isActive ? 'ACTIVE' : 'INACTIVE'}
                      className={c.isActive ? 'badge-success' : 'badge-neutral'}
                    />
                  </td>
                  {canModify && (
                    <td>
                      {c.isActive && (
                        <button
                          type="button"
                          className="btn btn-danger-ghost"
                          onClick={() => void handleDeactivate(c.id, c.name)}
                        >
                          Deactivate
                        </button>
                      )}
                    </td>
                  )}
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </>
  );
}
